import {
  ORDER_BOARD_STAGE_PREFIX,
  orderColumnKey,
  type OrderBoardStage,
  type OrderListItem,
  type OrderStatus,
} from "@school-sales-ai/shared";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";
import { ORDER_STATUS_META } from "@/lib/order-status-meta";

/**
 * One column of the /pedidos board.
 *
 * Five are the statuses and are fixed — the agent, the stock and the backend's
 * `canTransition` depend on what each means. The rest are the client's own
 * columns, and every one of them is a sub-stage of `confirmed`: an order in
 * "En camino" is still `confirmed` to everything outside this board.
 */
export type OrderBoardColumn = {
  key: string;
  label: string;
  /** The status an order in this column has. `confirmed` for every custom column. */
  status: OrderStatus;
  /** The custom column's id; null for a status column. */
  stageId: string | null;
  /** Solid dot class, for the header and the filter. */
  swatchClass: string;
};

function stageSwatch(color: string | null): string {
  return PIPELINE_COLOR_OPTIONS.find((c) => c.name === color)?.swatch ?? "bg-ink-300";
}

function statusColumn(status: OrderStatus): OrderBoardColumn {
  const meta = ORDER_STATUS_META[status];
  return { key: status, label: meta.label, status, stageId: null, swatchClass: meta.swatch };
}

/** The board's columns, left to right: custom columns right after "Por entregar". */
export function buildOrderColumns(stages: OrderBoardStage[]): OrderBoardColumn[] {
  const custom = [...stages]
    .sort((a, b) => a.position - b.position)
    .map(
      (st): OrderBoardColumn => ({
        key: `${ORDER_BOARD_STAGE_PREFIX}${st.id}`,
        label: st.label,
        status: "confirmed",
        stageId: st.id,
        swatchClass: stageSwatch(st.color),
      }),
    );
  return [
    statusColumn("open"),
    statusColumn("confirmed"),
    ...custom,
    statusColumn("delivered"),
    statusColumn("cancelled"),
    statusColumn("abandoned"),
  ];
}

export type OrderDropAction =
  | { type: "no-op" }
  | { type: "board-stage"; boardStageId: string | null }
  | { type: "deliver" }
  | { type: "cancel" }
  | { type: "refused"; reason: string };

/**
 * What dropping an order on a column does.
 *
 * Mirrors the backend's `ALLOWED_TRANSITIONS` (`order-rules.ts`) and nothing
 * more: the server refuses anything else anyway, but a drop that animates and
 * then snaps back reads as a bug, while a toast that says why reads as a rule.
 *
 * Two valid server transitions are deliberately NOT offered here: open →
 * abandoned (no CRM endpoint — "Cancelado" already closes a cart by hand) and
 * any confirmation, which belongs to the conversation, not to a drag.
 */
export function resolveOrderDrop(
  order: { status: OrderStatus; boardStageId: string | null },
  target: OrderBoardColumn,
): OrderDropAction {
  if (orderColumnKey(order) === target.key) return { type: "no-op" };

  switch (order.status) {
    case "confirmed":
      if (target.status === "confirmed") {
        return { type: "board-stage", boardStageId: target.stageId };
      }
      if (target.status === "delivered") return { type: "deliver" };
      if (target.status === "cancelled") return { type: "cancel" };
      if (target.status === "open") {
        return { type: "refused", reason: "Un pedido confirmado no vuelve a ser carrito." };
      }
      return {
        type: "refused",
        reason: "Solo un carrito abierto puede quedar abandonado. Usa «Cancelado».",
      };

    case "open":
      if (target.status === "cancelled") return { type: "cancel" };
      if (target.status === "confirmed") {
        return {
          type: "refused",
          reason: "Un carrito se confirma en la conversación con el cliente, no desde el tablero.",
        };
      }
      if (target.status === "delivered") {
        return { type: "refused", reason: "Un carrito tiene que confirmarse antes de entregarse." };
      }
      return {
        type: "refused",
        reason: "Los carritos se marcan como abandonados solos. Para cerrarlo usa «Cancelado».",
      };

    case "delivered":
      return { type: "refused", reason: "Un pedido entregado ya no cambia de estado." };

    default:
      return { type: "refused", reason: "Este pedido ya está cerrado." };
  }
}

/** The columns an order can actually be moved to — dims the rest while dragging. */
export function moveTargetsFor(
  order: { status: OrderStatus; boardStageId: string | null },
  columns: OrderBoardColumn[],
): OrderBoardColumn[] {
  return columns.filter((c) => {
    const action = resolveOrderDrop(order, c);
    return action.type !== "no-op" && action.type !== "refused";
  });
}

/** Buckets the loaded orders by the column each one sits in. */
export function groupOrdersByColumn(
  items: OrderListItem[],
  columns: OrderBoardColumn[],
): Record<string, OrderListItem[]> {
  const grouped: Record<string, OrderListItem[]> = Object.fromEntries(
    columns.map((c): [string, OrderListItem[]] => [c.key, []]),
  );
  for (const order of items) {
    // A column deleted since the board loaded is guarded against rather than
    // crashing on: the order simply waits for the refetch.
    grouped[orderColumnKey(order)]?.push(order);
  }
  return grouped;
}

/**
 * «2× Pizza, 1× Refresco +2 más» — the lines a card has room for, then how many
 * more units the order carries beyond them.
 */
export function formatLinesPreview(order: OrderListItem): string {
  const shown = order.linesPreview.map((l) => `${l.quantity}× ${l.productName}`).join(", ");
  const shownUnits = order.linesPreview.reduce((n, l) => n + l.quantity, 0);
  const rest = order.itemCount - shownUnits;
  return rest > 0 ? `${shown} +${rest} más` : shown;
}
